/**
 * Shared order of files and tests inside one temario node.
 * archivos / testRefs stay as data sources; contenidoOrden is the cross-type sequence.
 */

/**
 * @typedef {{ kind: "archivo", nombre: string } | { kind: "test", titulo: string }} ContenidoOrdenEntry
 */

/**
 * @typedef {object} ContenidoListItem
 * @property {"archivo" | "test"} kind
 * @property {number} index
 * @property {number} sourceIndex
 * @property {{ nombre: string, tipo?: string, url?: string, storageKey?: string }} [archivo]
 * @property {{ titulo: string, bloque: string }} [testRef]
 */

/**
 * @param {unknown} raw
 * @returns {ContenidoOrdenEntry | null}
 */
export function normalizeContenidoOrdenEntry(raw) {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const rec = /** @type {Record<string, unknown>} */ (raw);
  if (rec.kind === "archivo") {
    const nombre = typeof rec.nombre === "string" ? rec.nombre.trim() : "";
    return nombre ? { kind: "archivo", nombre } : null;
  }
  if (rec.kind === "test") {
    const titulo = typeof rec.titulo === "string" ? rec.titulo.trim() : "";
    return titulo ? { kind: "test", titulo } : null;
  }
  return null;
}

/**
 * @param {{ nombre?: string } | null | undefined} archivo
 * @returns {string}
 */
export function archivoOrdenKey(archivo) {
  return typeof archivo?.nombre === "string" ? archivo.nombre.trim() : "";
}

/**
 * @param {{ titulo?: string } | null | undefined} testRef
 * @returns {string}
 */
export function testOrdenKey(testRef) {
  return typeof testRef?.titulo === "string" ? testRef.titulo.trim() : "";
}

/**
 * @param {ContenidoListItem[]} items
 * @returns {ContenidoOrdenEntry[]}
 */
export function contenidoOrdenFromItems(items) {
  if (!Array.isArray(items)) {
    return [];
  }
  /** @type {ContenidoOrdenEntry[]} */
  const out = [];
  for (const item of items) {
    if (item.kind === "test") {
      const titulo = testOrdenKey(item.testRef);
      if (titulo) {
        out.push({ kind: "test", titulo });
      }
    } else {
      const nombre = archivoOrdenKey(item.archivo);
      if (nombre) {
        out.push({ kind: "archivo", nombre });
      }
    }
  }
  return out;
}

/**
 * @param {{
 *   archivos?: unknown[],
 *   testRefs?: unknown[],
 *   contenidoOrden?: unknown,
 * } | null | undefined} node
 * @returns {ContenidoListItem[]}
 */
export function listContenidoItems(node) {
  const archivos = Array.isArray(node?.archivos) ? node.archivos : [];
  const testRefs = Array.isArray(node?.testRefs) ? node.testRefs : [];
  const usedArchivos = new Set();
  const usedTests = new Set();
  /** @type {Omit<ContenidoListItem, "index">[]} */
  const items = [];

  const orden = Array.isArray(node?.contenidoOrden) ? node.contenidoOrden : [];
  for (const raw of orden) {
    const entry = normalizeContenidoOrdenEntry(raw);
    if (!entry) {
      continue;
    }
    if (entry.kind === "archivo") {
      const sourceIndex = archivos.findIndex(
        (archivo, idx) => !usedArchivos.has(idx) && archivoOrdenKey(archivo) === entry.nombre,
      );
      if (sourceIndex < 0) {
        continue;
      }
      usedArchivos.add(sourceIndex);
      items.push({
        kind: "archivo",
        sourceIndex,
        archivo: /** @type {ContenidoListItem["archivo"]} */ (archivos[sourceIndex]),
      });
      continue;
    }
    const sourceIndex = testRefs.findIndex(
      (ref, idx) => !usedTests.has(idx) && testOrdenKey(ref) === entry.titulo,
    );
    if (sourceIndex < 0) {
      continue;
    }
    usedTests.add(sourceIndex);
    items.push({
      kind: "test",
      sourceIndex,
      testRef: /** @type {ContenidoListItem["testRef"]} */ (testRefs[sourceIndex]),
    });
  }

  archivos.forEach((archivo, sourceIndex) => {
    if (usedArchivos.has(sourceIndex) || !archivoOrdenKey(archivo)) {
      return;
    }
    items.push({
      kind: "archivo",
      sourceIndex,
      archivo: /** @type {ContenidoListItem["archivo"]} */ (archivo),
    });
  });
  testRefs.forEach((testRef, sourceIndex) => {
    if (usedTests.has(sourceIndex) || !testOrdenKey(testRef)) {
      return;
    }
    items.push({
      kind: "test",
      sourceIndex,
      testRef: /** @type {ContenidoListItem["testRef"]} */ (testRef),
    });
  });

  return items.map((item, index) => ({ ...item, index }));
}

/**
 * @param {{
 *   archivos?: unknown[],
 *   testRefs?: unknown[],
 *   contenidoOrden?: unknown,
 * } | null | undefined} node
 * @param {"archivo" | "test"} kind
 * @returns {ContenidoListItem[]}
 */
export function listContenidoItemsByKind(node, kind) {
  if (kind !== "archivo" && kind !== "test") {
    return [];
  }
  return listContenidoItems(node).filter((item) => item.kind === kind);
}

/**
 * Swaps one file or test with its neighbor in the shared sequence.
 * Writes only contenidoOrden. archivos and testRefs stay in place.
 * @param {Record<string, unknown>} node
 * @param {"archivo" | "test"} kind
 * @param {number} sourceIndex
 * @param {number} delta
 * @returns {boolean}
 */
export function moveContenidoItemBySource(node, kind, sourceIndex, delta) {
  if (!node || typeof node !== "object" || (kind !== "archivo" && kind !== "test")) {
    return false;
  }
  if (typeof sourceIndex !== "number" || !Number.isInteger(sourceIndex)) {
    return false;
  }
  if (typeof delta !== "number" || !Number.isInteger(delta) || delta === 0) {
    return false;
  }

  const items = listContenidoItems(node);
  const from = items.findIndex((item) => item.kind === kind && item.sourceIndex === sourceIndex);
  const target = from + delta;
  if (from < 0 || target < 0 || target >= items.length) {
    return false;
  }

  const next = [...items];
  const swapped = next[from];
  next[from] = next[target];
  next[target] = swapped;
  node.contenidoOrden = contenidoOrdenFromItems(next);
  return true;
}

/**
 * @param {Record<string, unknown>} node
 * @param {ContenidoListItem[]} items
 */
export function applyContenidoItemsToNode(node, items) {
  const list = Array.isArray(items) ? items : [];
  node.archivos = list.filter((item) => item.kind === "archivo").map((item) => item.archivo);
  node.testRefs = list.filter((item) => item.kind === "test").map((item) => item.testRef);
  node.contenidoOrden = contenidoOrdenFromItems(list);
}

/**
 * @param {Record<string, unknown>} node
 * @returns {Record<string, unknown>}
 */
export function syncContenidoOrdenOnNode(node) {
  if (!node || typeof node !== "object") {
    return node;
  }
  applyContenidoItemsToNode(node, listContenidoItems(node));
  return node;
}

/**
 * @param {unknown} raw
 * @returns {ContenidoOrdenEntry[]}
 */
export function sanitizeContenidoOrdenEntries(raw) {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map((entry) => normalizeContenidoOrdenEntry(entry))
    .filter((entry) => entry !== null);
}
